/**
 * Guard for URL fragments that get forwarded into `navigate()`.
 *
 * The portal keeps a hash-router compatibility shim (`HashCatch` in App.tsx)
 * that replays `#/some/route` as a real route. The fragment is entirely
 * attacker-controlled, and a `#/` prefix check alone is not enough: React
 * Router reads a target of `//host` or `/\host` as protocol-relative and
 * navigates off-origin, so `https://portal/#//evil.com` is an open redirect.
 *
 * Two of the advisories behind this have no patched release on the 6.x line
 * (GHSA-wrjc-x8rr-h8h6, GHSA-jjmj-jmhj-qwj2), so the target has to be proven
 * internal here rather than left to the router version.
 */

/** Anything below U+0020. Browsers strip these while parsing a URL, which
 * can smuggle a prefix past the leading-slash checks below. */
const hasControlCharacter = (value: string) =>
  value.split("").some((character) => character.charCodeAt(0) < 0x20);

export const isInternalPath = (target: string) =>
  target.startsWith("/") &&
  // `//host` is protocol-relative and leaves the origin.
  !target.startsWith("//") &&
  // `/\host` is the same bypass via a delimiter the browser normalises;
  // no portal route contains a backslash.
  !target.includes("\\") &&
  !hasControlCharacter(target);
